import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton, Reveal } from '@/components/atoms';
import { type FaqItem, FaqRow } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { useFaqAccordion } from '@/hooks/useFaqAccordion';

type Props = {
  /** The id the enclosing section is labelled by; unique per page. */
  titleId: string;
  eyebrow: string;
  title: string;
  lede: string;
  items: readonly FaqItem[];
  /** The way out beside the questions. */
  action: { label: string; to: string };
};

/**
 * A page's questions, answered on it.
 *
 * One band serving the hub and each service page, which is why every word in
 * it is a prop. The alternative was a near-identical organism per page
 * differing only in its heading, and the first time the accordion behaviour
 * changed, every one of those files would have had to change with it.
 *
 * The answers are fed to `faqSchema` from the same array the band renders, by
 * the page above it. Google only shows an FAQ rich result when the questions
 * are visible on the page, and building both from one array is what guarantees
 * they are — see `FaqRow`, which keeps every closed answer in the DOM for the
 * same reason.
 */
export function ServiceFaqBand({ titleId, eyebrow, title, lede, items, action }: Props) {
  const { openIndex, toggle } = useFaqAccordion();

  return (
    <Section
      labelledBy={titleId}
      backdrop={{ picture: introVehicles.impreza.image, placement: 'left' }}
    >
      <Grid container spacing={{ xs: 8, md: 12 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Reveal direction="left">
            <Stack spacing={4} sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
              <Typography variant="overline" sx={{ color: 'primary.main' }}>
                {eyebrow}
              </Typography>
              <Typography id={titleId} variant="h2" component="h2">
                {title}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {lede}
              </Typography>
              <AppButton to={action.to} appVariant="secondary" sx={{ alignSelf: 'flex-start' }}>
                {action.label}
              </AppButton>
            </Stack>
          </Reveal>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {items.map((item, i) => (
              <FaqRow
                key={item.question}
                item={item}
                open={openIndex === i}
                onToggle={() => toggle(i)}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );
}
